import { computed, ref } from "vue";
import NykButton from "../NykButton/NykButton.vue"
import NykTag from "../NykTag/NykTag.vue"

export default {
    name: "NykCard",
    components: {
        NykButton,
        NykTag
    },
    props: {
        size: {
            type: String,
            default: "medium",
            validator: (value) => ["small", "medium", "large"].includes(value)
        },
        variant: {
            type: String,
            default: "neutral",
            validator: (value) => [
                "primary",
                "primary-dark",
                "accent",
                "secondary",
                "neutral",
                "light"
            ].includes(value)
        },
        type: {
            type: String,
            default: "default",
            validator: (value) => ["default", "video", "image"].includes(value)
        },
        ariaLabel: {
            type: String,
            default: ""
        },
        title: {
            type: String,
            default: ""
        },
        description: {
            type: String,
            default: ""
        },
        footerLinkHref: {
            type: String,
            default: ""
        },
        footerLinkText: {
            type: String,
            default: ""
        },
        footerLinkTarget: {
            type: String,
            default: "_self"
        },
        footerLinkAriaLabel: {
            type: String,
            default: ""
        },
        mediaSrc: {
            type: String,
            default: ""
        },
        mediaAriaLabel: {
            type: String,
            default: ""
        },
        videoThumbnailUrl: {
            type: String,
            default: ""
        },
        customClass: {
            type: String,
            default: ""
        },
        metaTag: {
            type: String,
            default: ""
        },
        metaCategory: {
            type: String,
            default: ""
        },
        metaDate: {
            type: String,
            default: ""
        },
        linkedInUrl: {
            type: String,
            default: ""
        },
        twitterUrl: {
            type: String,
            default: ""
        },
        facebookUrl: {
            type: String,
            default: ""
        },
        phone: {
            type: String,
            default: ""
        },
        phoneLabel: {
            type: String,
            default: ""
        },
        email: {
            type: String,
            default: ""
        },
        emailLabel: {
            type: String,
            default: ""
        }
    },
    emits: ["link-click", "tag-close"],
    setup(props, { emit, slots }) {
        const videoElement = ref(null);
        const thumbnailVisible = ref(true);

        const outerClasses = computed(() => {
            const classes = [
                `nyk-generic-card`,
                props.customClass
            ];

            return classes.filter(Boolean).join(" ");
        });

        const innerClasses = computed(() => {
            const classes = [
                `nyk-generic-card__inner`,
                `nyk-generic-card__inner--${props.size}`
            ];

            // Add type modifier
            if (props.type !== "default") {
                classes.push(`nyk-generic-card__inner--${props.type}`);
            }

            // Add variant (maps to color)
            if (props.variant !== "neutral") {
                classes.push(`nyk-generic-card__inner--${props.variant}`);
            }

            return classes.filter(Boolean).join(" ");
        });

        const hasMetaSlot = computed(() => !!slots.meta);
        const hasFooterSlot = computed(() => !!slots.footer);
        const hasMediaSlot = computed(() => !!slots.media);
        const hasHeaderSlot = computed(() => !!slots.header);
        const hasContentSlot = computed(() => !!slots.default);

        const hasMeta = computed(() => {
            return props.metaTag || props.metaCategory || props.metaDate || hasMetaSlot.value;
        });

        const hasSocial = computed(() => {
            return props.linkedInUrl || props.twitterUrl || props.facebookUrl;
        });

        const hasContactInfo = computed(() => {
            return !!(props.phone || props.email);
        });

        const tagSize = computed(() => {
            return props.size === 'large' ? 'medium' : 'small';
        });

        const computedTagVariant = computed(() => {
            // Determine tag variant based on card background for WCAG contrast
            const darkBackgroundVariants = ['primary', 'primary-dark', 'accent'];
            return darkBackgroundVariants.includes(props.variant) ? 'light' : 'neutral';
        });

        // Determine hyperlink variant based on card variant
        const hyperlinkVariant = computed(() => {
            const lightTextVariants = ['primary', 'primary-dark', 'accent'];
            return lightTextVariants.includes(props.variant) ? 'hyperlink-neutral' : 'hyperlink';
        });

        const socialButtonVariant = computed(() => {
            const darkBackgroundVariants = ['primary', 'primary-dark', 'accent'];
            return darkBackgroundVariants.includes(props.variant) ? 'outline' : 'primary-outline';
        });

        const showThumbnail = computed(() => {
            return props.type === 'video' && props.videoThumbnailUrl && props.videoThumbnailUrl.trim() !== '';
        });

        const handleLinkClick = (event) => {
            emit("link-click", event);
        };

        const handleTagClose = () => {
            emit("tag-close");
        };

        const playVideo = () => {
            thumbnailVisible.value = false;
        };

        const onAfterLeave = () => {
            if (videoElement.value) {
                videoElement.value.play().catch(error => {
                    console.error('Video playback failed:', error);
                });
            }
        };

        return {
            outerClasses,
            innerClasses,
            hasMetaSlot,
            hasFooterSlot,
            hasMediaSlot,
            hasHeaderSlot,
            hasContentSlot,
            hasMeta,
            hasSocial,
            hasContactInfo,
            tagSize,
            computedTagVariant,
            hyperlinkVariant,
            socialButtonVariant,
            showThumbnail,
            handleLinkClick,
            handleTagClose,
            playVideo,
            onAfterLeave,
            videoElement,
            thumbnailVisible
        };
    }
};
